<!-- =========================================================================================
  File Name: DashboardAnalytics.vue
  Description: Dashboard Analytics
  ----------------------------------------------------------------------------------------
  Item Name: Galileo Computing VueJS Dashboard
  Author: Ian Kibet
  Author URL: http://ian.galileoc.co.ke
========================================================================================== -->

<template>
  <div>
    <div class="hidden">
      {{ plotThisYearExpenses }}
      {{ plotThisWeekExpenses }}
      {{ plotThisWeekHarvest }}
    </div>
    <div>
      <div class="vx-row">
        <div class="vx-col w-full lg:w-1/2">
          <div class="vx-row">
            <div
              class="vx-col w-full sm:w-1/2 md:w-1/2 lg:w-1/2 xl:w-1/2 mb-base"
            >
              <vx-card v-if="load.expenses.weekly" class="mb-base">
                <div class="m-4">
                  <skeleton-box height="40px" width="100px"></skeleton-box>

                  <skeleton-box class="mt-4" height="20px"></skeleton-box>
                  <skeleton-box class="mt-2" height="20px"></skeleton-box>
                  <skeleton-box class="mt-2" height="20px"></skeleton-box>
                  <skeleton-box class="mt-2" height="20px"></skeleton-box>
                </div>
              </vx-card>
              <statistics-card-line
                v-else
                class="lg:mb-6 sm:mb-4"
                icon="PercentIcon"
                icon-right
                :statistic="expenses.weekly"
                statisticTitle="This week's expenses"
                color="warning"
                type="area"
                :chartData="[
                  {
                    name: 'Daily Expenses',
                    data: plotThisWeekExpenses,
                  },
                ]"
              />
            </div>
            <div
              class="vx-col w-full sm:w-1/2 md:w-1/2 lg:w-1/2 xl:w-1/2 mb-base"
            >
              <vx-card v-if="load.harvests.weekly" class="mb-base">
                <div class="m-4">
                  <skeleton-box height="40px" width="100px"></skeleton-box>

                  <skeleton-box class="mt-4" height="20px"></skeleton-box>
                  <skeleton-box class="mt-2" height="20px"></skeleton-box>
                  <skeleton-box class="mt-2" height="20px"></skeleton-box>
                  <skeleton-box class="mt-2" height="20px"></skeleton-box>
                </div>
              </vx-card>
              <statistics-card-line
                v-else
                class="lg:mb-6 sm:mb-4"
                icon="FeatherIcon"
                icon-right
                :statistic="harvests.weekly"
                statisticTitle="This week's harvests"
                color="success"
                type="area"
                :chartData="[
                  {
                    name: 'Daily Harvests',
                    data: plotThisWeekHarvest,
                  },
                ]"
              />
            </div>
          </div>
          <div class="mb-6 ml-2">
            <span
              >Chart data generated from
              <strong>{{ this.week.x | date }} </strong> and
              <strong>{{ Date() | date }}</strong></span
            >
          </div>
        </div>
        <div class="vx-col w-full lg:w-1/2">
          <vx-card v-if="load.harvests.daily" class="mb-base">
            <div>
              <skeleton-box height="30px" width="60px"></skeleton-box>
              <skeleton-box class="mt-2" height="18px"></skeleton-box>
            </div>
          </vx-card>
          <statistics-card-line
            v-else
            hideChart
            class="mb-base"
            icon="ArrowDownIcon"
            icon-right
            :statistic="harvests.today"
            statisticTitle="Today's Harvests (m3)"
            color="success"
          />
          <vx-card v-if="!farmData" class="mb-base">
            <div>
              <skeleton-box height="30px" width="60px"></skeleton-box>
              <skeleton-box class="mt-2" height="18px"></skeleton-box>
            </div>
          </vx-card>
          <statistics-card-line
            v-else
            hideChart
            class="mb-base"
            icon="CalendarIcon"
            icon-right
            :statistic="thisMonthsHarvests"
            :statisticTitle="`${thisMonth}'s harvest (m3)`"
            color="primary"
          />
        </div>
      </div>
      <div class="vx-row">
        <div class="vx-col w-full lg:w-1/3">
          <vx-card v-if="load.harvests.monthly" class="mb-base">
            <div>
              <skeleton-box height="30px" width="60px"></skeleton-box>
              <skeleton-box class="mt-2" height="18px"></skeleton-box>
            </div>
          </vx-card>
          <statistics-card-line
            v-else
            hideChart
            class="mb-base"
            icon="FeatherIcon"
            icon-right
            :statistic="farmStat"
            :statisticTitle="`Farm${farmStat.length === 1 ? '' : 's'}`"
            color="warning"
          />
        </div>
        <div class="vx-col w-full lg:w-1/3">
          <vx-card v-if="!(operatorData instanceof Array)" class="mb-base">
            <div>
              <skeleton-box height="30px" width="60px"></skeleton-box>
              <skeleton-box class="mt-2" height="18px"></skeleton-box>
            </div>
          </vx-card>
          <statistics-card-line
            v-else
            hideChart
            class="mb-base"
            icon="ToolIcon"
            icon-right
            :statistic="operatorData.length"
            :statisticTitle="`Operator${operatorData.length === 1 ? '' : 's'}`"
            color="warning"
          />
        </div>
        <div class="vx-col w-full lg:w-1/3">
          <vx-card v-if="!(truckData instanceof Array)" class="mb-base">
            <div>
              <skeleton-box height="30px" width="60px"></skeleton-box>
              <skeleton-box class="mt-2" height="18px"></skeleton-box>
            </div>
          </vx-card>
          <statistics-card-line
            v-else
            hideChart
            class="mb-base"
            icon="TruckIcon"
            icon-right
            :statistic="truckData.length"
            :statisticTitle="`Truck${truckData.length === 1 ? '' : 's'}`"
            color="secondary"
          />
        </div>
      </div>
      <div class="vx-row">
        <div class="vx-col w-full lg:w-2/3">
          <vx-card v-if="expensesCategories.length < 1" class="mb-base">
            <div>
              <skeleton-box height="40px" width="250px"></skeleton-box>
              <skeleton-box class="mt-4" height="250px"></skeleton-box>
            </div>
          </vx-card>
          <vx-card v-else title="Farms Expenses Overview">
            <div class="flex">
              <span class="flex items-center"
                ><div class="h-3 w-3 rounded-full mr-1 bg-success"></div>
                <span>Assets</span></span
              >
              <span class="flex items-center ml-4"
                ><div class="h-3 w-3 rounded-full mr-1 bg-danger"></div>
                <span>Liability</span></span
              >
            </div>
            <vue-apex-charts
              type="bar"
              height="277"
              :options="expensesChartOptions"
              :series="plotThisYearExpenses"
            />
          </vx-card>
        </div>
        <div class="vx-col w-full lg:w-1/3 lg:mt-0 mt-base">
          <vx-card
            title="Quick Actions"
            class="no-body mb-base overflow-hidden pb-6 hidden lg:block"
          >
          </vx-card>
          <vx-card
            class="overflow-hidden text-white bg-primary-gradient hidden lg:block"
          >
            <template slot="no-body">
              <div>
                <VuePerfectScrollbar
                  ref="qAPS"
                  class="scroll-area px-8 py-6"
                  :settings="settings"
                  :key="$vs.rtl"
                >
                  <ul ref="qA">
                    <li
                      @click="action.action"
                      class="cursor-pointer hover:bg-secondary"
                      :class="{
                        'flex justify-between flex-row-reverse items-center': true,
                        'text-center': !true,
                      }"
                      v-for="(action, index) in quickActions"
                      :key="index"
                    >
                      <feather-icon
                        :icon="action.icon"
                        class="p-3 inline-flex rounded-full"
                        :class="[`text-${action.color}`]"
                        :style="{
                          background: `rgba(var(--vs-${action.color}),.15)`,
                        }"
                      ></feather-icon>
                      <div class="truncate">
                        <strong>{{ action.title }}</strong>
                      </div>
                    </li>
                  </ul>
                </VuePerfectScrollbar>
              </div>
            </template>
          </vx-card>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import SkeletonBox from "@/components/SkeletonBox.vue";
import VueApexCharts from "vue-apexcharts";
import StatisticsCardLine from "@/components/statistics-cards/StatisticsCardLine.vue";
import VuePerfectScrollbar from "vue-perfect-scrollbar";
// import analyticsData from "./ui-elements/card/analyticsData.js";
import ChangeTimeDurationDropdown from "@/components/ChangeTimeDurationDropdown.vue";
import VxTimeline from "@/components/timeline/VxTimeline";
const { endpoint } = require("@/../config.js");

import p from "@/db/pouch";
import { months } from "moment";

export default {
  data() {
    return {
      farms: [],

      sales_today: 0,
      total_products: 0,
      total_customers: 0,
      recent_sales: [],
      pending_jobs: [],

      checkpointReward: {},
      subscribersGained: {},
      ordersRecevied: {},
      salesBarSession: {},
      supportTracker: {},
      productsOrder: {},
      salesRadar: {},

      expensesCategories: [],
      dispatchedOrders: [],

      dateRange: {
        startDate: this.getStartDate(),
        endDate: this.getDate(Date.now()),
      },

      settings: {
        // perfectscrollbar settings
        maxScrollbarLength: 60,
        wheelSpeed: 0.6,
      },

      quickActions: [
        {
          type: "page",
          iconType: "feather",
          icon: "FeatherIcon",
          color: "secondary",
          title: "Open Farms",
          action: () => this.$router.push("/farms").catch({}),
        },
        {
          type: "page",
          iconType: "feather",
          icon: "PlusIcon",
          color: "secondary",
          title: "Create New Farm",
          action: () => this.$router.push("/farms").catch({}),
        },
        {
          type: "page",
          iconType: "feather",
          icon: "PercentIcon",
          color: "secondary",
          title: "Manage Expenses",
          action: () => this.$router.push("/expenses").catch({}),
        },
        {
          type: "page",
          iconType: "feather",
          icon: "ToolIcon",
          color: "secondary",
          title: "Manage Operators",
          action: () => this.$router.push("/operators").catch({}),
        },
      ],

      harvestsThisWeek: [],
      harvestsThisMonth: [],

      expensesThisWeek: [],
      expensesThisYear: [],

      milliseconds: 1000,

      day: 86400000, // In ms
      month: 2592000,

      monthsInYear: 12,

      load: {
        harvests: {
          daily: true,
          weekly: true,
          monthly: false,
        },
        expenses: {
          daily: true,
          weekly: true,
          monthly: false,
          yearly: true,
        },
      },
      harvests: {
        today: 0,
        weekly: 0,
        weeklySeries: [],
      },
      expenses: {
        today: 0,
        weekly: 0,
        weeklySeries: [],
      },

      timeOffset: 3, // in Hours

      today: new Date(),
      monthsShort: [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ],
      months: [
        "January", //0
        "February", //1
        "March", //2
        "April", //3
        "May", //4
        "June", //5
        "July", //6
        "August", //7
        "September", //8
        "October", //9
        "November", //10
        "December", //11
      ],
      monthDays: [
        31, //0
        28, //1
        31, //2
        30, //3
        31, //4
        30, //5
        31, //6
        31, //7
        30, //8
        31, //9
        30, //10
        31, //11
      ],
    };
  },
  pouch: {
    farmData() {
      return {
        database: process.env.VUE_APP_DB,
        selector: { z: 6 },
      };
    },
    operatorData() {
      return {
        database: process.env.VUE_APP_DB,
        selector: { z: 3 },
      };
    },
    truckData() {
      return {
        database: process.env.VUE_APP_DB,
        selector: { z: 2 },
      };
    },
    harvestDataThisMonth() {
      return {
        database: process.env.VUE_APP_DB,
        selector: {
          z: 9,
          y: {
            $gt: this.firstDay.getTime(),
            $lt: this.lastDay.getTime(),
          },
        },
      };
    },
    harvestDataThisWeek() {
      return {
        database: process.env.VUE_APP_DB,
        selector: {
          z: 9,
          y: {
            $gt: this.week.x.getTime(),
            $lt: this.week.y.getTime(),
          },
        },
      };
    },
    expenseDataThisWeek() {
      return {
        database: process.env.VUE_APP_DB,
        selector: {
          z: 7,
          y: {
            $gt: this.week.x.getTime(),
            $lt: this.week.y.getTime(),
          },
        },
      };
    },
    expenseDataThisYear() {
      return {
        database: process.env.VUE_APP_DB,
        selector: {
          z: 7,
          y: {
            $gt: this.year.x.getTime(),
            $lt: this.year.y.getTime(),
          },
        },
      };
    },
  },
  watch: {
    harvestDataThisWeek(v) {
      this.decrypt("harvestsThisWeek", v);
    },
    expenseDataThisWeek(v) {
      this.decrypt("expensesThisWeek", v);
    },
    async expenseDataThisYear(v) {
      await this.decrypt("expensesThisYear", v);
      // this.plotThisYearExpenses();
    },
    async harvestDataThisMonth(v) {
      this.decrypt("harvestsThisMonth", v);
    },
  },
  computed: {
    expensesChartOptions() {
      return {
        grid: {
          borderColor: "#ebebeb",
          padding: {
            left: 0,
            right: 0,
          },
        },
        legend: {
          show: false,
        },
        dataLabels: {
          enabled: false,
        },
        chart: {
          stacked: true,
          type: "bar",
          toolbar: { show: false },
        },
        colors: ["#28C76F", "#EA5455"],
        plotOptions: {
          bar: {
            columnWidth: "10%",
          },
        },
        xaxis: {
          labels: {
            style: {
              cssClass: "text-grey fill-current",
            },
          },
          axisTicks: {
            show: false,
          },
          categories: this.expensesCategories,
          axisBorder: {
            show: false,
          },
        },
        yaxis: {
          tickAmount: 5,
          labels: {
            style: {
              cssClass: "text-grey fill-current",
            },
          },
        },
        tooltip: {
          x: { show: false },
        },
      };
    },
    thisMonthsHarvests() {
      let harvestCount = 0;
      if (this.harvestsThisMonth.length === 0) return harvestCount;
      this.load.harvests.monthly = true;
      this.harvestsThisMonth.map((harvest) => {
        harvestCount += parseInt(harvest.c.b) || 0;
      });
      this.load.harvests.monthly = false;
      return harvestCount;
    },
    farmStat() {
      return this.farmData ? this.farmData.length : 0;
    },
    thisMonth() {
      return this.months[this.today.getMonth()] || "This month";
    },
    week() {
      let date = new Date();
      date.setDate(date.getDate() - 6);
      date.setHours(0, 0, 0, 0);
      let tlastHour = this.today;
      tlastHour.setHours(23, 0, 0, 0);
      let firstDay = date;
      let lastDay = this.today;

      return {
        x: firstDay,
        y: lastDay,
      };
    },
    year() {
      let date = new Date();
      date.setFullYear(date.getFullYear() - 1);
      date.setDate(date.getDate() - 6);
      date.setHours(0, 0, 0, 0);
      let tlastHour = this.today;
      tlastHour.setHours(23, 0, 0, 0);
      let firstDay = date;
      let lastDay = this.today;

      return {
        x: firstDay,
        y: lastDay,
      };
    },
    firstDay() {
      return new Date(this.today.getFullYear(), this.today.getMonth(), 1);
    },
    lastDay() {
      return new Date(this.today.getFullYear(), this.today.getMonth() + 1, 0);
    },
    plotThisWeekHarvest() {
      let series = new Array(7).fill(0);
      let weeklyHarvests = this.harvestsThisWeek;
      if (weeklyHarvests.length === 0) return series;
      this.load.harvests.weekly = true;
      this.load.harvests.daily = true;

      let dayLoop = this.getDays();

      let today = new Date();
      today.setHours(0, 0, 0, 0);
      let weeklyVolume = 0;

      series = dayLoop.map((s) => {
        const filteredData = this.filterData(
          weeklyHarvests,
          (e) => e.y < s.z && e.y > s.x
        );
        let totalVolume = 0;
        if (filteredData.length === 0) return totalVolume;
        filteredData.map((harvest) => {
          totalVolume += parseInt(harvest.c.b) || 0;
        });
        if (s.x === today.getTime()) {
          this.harvests.today += totalVolume;
        }
        weeklyVolume += totalVolume;
        return totalVolume;
      });
      this.harvests.weekly = weeklyVolume;
      this.load.harvests.weekly = false;
      this.load.harvests.daily = false;
      return series;
    },
    plotThisWeekExpenses() {
      let series = [];
      let weeklyExpenses = this.expensesThisWeek;
      if (weeklyExpenses.length === 0) return series;
      this.load.expenses.weekly = true;
      this.load.expenses.daily = true;

      let dayLoop = this.getDays();

      let today = new Date();
      today.setHours(0, 0, 0, 0);
      let weeklyVolume = 0;

      series = dayLoop.map((s) => {
        const filteredData = this.filterData(
          weeklyExpenses,
          (e) => e.y < s.z && e.y > s.x
        );
        let totalVolume = 0;
        if (filteredData.length === 0) return totalVolume;
        filteredData.map((expense) => {
          totalVolume += parseInt(expense.a) || 0;
        });
        if (s.x === today.getTime()) {
          this.expenses.today += totalVolume;
        }
        weeklyVolume += totalVolume;
        return totalVolume;
      });
      this.expenses.weekly = weeklyVolume;
      this.load.expenses.weekly = false;
      this.load.expenses.daily = false;
      return series;
    },
    plotThisYearExpenses() {
      let series = [];
      let seriesl = [];
      let yearlyExpenses = this.expensesThisYear;
      if (yearlyExpenses.length === 0) return series;
      this.load.expenses.yearly = true;

      let monthLoop = this.getMonths();

      let today = new Date();
      today.setHours(0, 0, 0, 0);
      let monthlyVolume = 0;

      monthLoop.map((s) => {
        const filteredData = this.filterData(
          yearlyExpenses,
          (e) => e.y < s.z && e.y > s.x
        );
        let totalVolume = 0;
        let totalAssets = 0;
        let totalLiability = 0;
        filteredData.map((expense) => {
          totalVolume += parseInt(expense.a) || 0;
          switch (expense.e) {
            case "Asset":
              totalAssets += parseInt(expense.a) || 0;
              break;
            case "Liability":
              totalLiability += parseInt(expense.a) || 0;
              break;
          }
        });

        monthlyVolume += totalVolume;
        series.push(totalAssets);
        seriesl.push(totalLiability);
        return totalVolume;
      });
      this.expenses.monthly = monthlyVolume;
      this.load.expenses.yearly = false;
      let xaxis = [];
      let monthsIndices = new Array(this.monthsInYear).fill(0);
      let currentMonth = this.today.getMonth();
      xaxis = monthsIndices.map((_, i) => {
        let a = currentMonth + i;
        if (a > this.monthsInYear - 1) {
          a = a - this.monthsInYear;
        }
        return this.monthsShort[a];
      });

      xaxis.reverse();
      this.expensesCategories = xaxis;

      return [
        {
          name: "Assets",
          data: series,
        },
        {
          name: "Liability",
          data: seriesl,
        },
      ];
    },
  },
  methods: {
    getMonths() {
      let initX = this.year.x.getTime();
      let x = initX;
      let px = 0;
      let z = x + this.month;

      let months = new Array(12).fill(0);

      return months.map((_, i) => {
        let t = this.monthDays[i] * this.day;
        px = x;
        x = px + t;
        z = x + t;
        let time = {
          x,
          z,
          d: z - x,
          t,
          n: this.monthDays[i],
        };
        return time;
      });
    },
    getDays() {
      let initX = this.week.x.getTime();
      let x = initX;
      let z = x + this.day;

      let days = new Array(7).fill(0);

      return days.map((_, i) => {
        z = z + (i == 0 ? 0 : this.day);
        x = initX + i * this.day;
        let time = {
          x,
          z,
          d: z - x,
        };
        return time;
      });
    },
    filterData(data, query) {
      if (!(data instanceof Array)) return data;
      return data.filter((e) => query(e));
    },
    log(msg, ...args) {
      console.log(msg, args);
    },
    getDate(x) {
      return this.$moment(x).format("DD MMM YYYY");
    },
    getStartDate() {
      var d = new Date();
      d.setHours(0, 0, 0, 0);
      return d;
    },
    async decrypt(k, v) {
      this[k] = await p.de(v);
    },
    timeConverter(UNIX_timestamp) {
      var a = new Date(UNIX_timestamp);
      var months = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ];
      var year = a.getFullYear();
      var month = months[a.getMonth()];
      var date = a.getDate();
      var hour = a.getHours();
      var min = a.getMinutes();
      var sec = a.getSeconds();
      var time = date + " " + month + " " + year + " " + hour + "00 Hrs"; // + ":" + min + ":" + sec;
      return time;
    },
  },
  components: {
    VueApexCharts,
    StatisticsCardLine,
    ChangeTimeDurationDropdown,
    VxTimeline,
    SkeletonBox,
    VuePerfectScrollbar,
  },
  created() {},
};
</script>

<style lang="scss">
/*! rtl:begin:ignore */
#dashboard-analytics {
  .greet-user {
    position: relative;

    .decore-left {
      position: absolute;
      left: 0;
      top: 0;
    }
    .decore-right {
      position: absolute;
      right: 0;
      top: 0;
    }
  }

  @media (max-width: 576px) {
    .decore-left,
    .decore-right {
      width: 140px;
    }
  }
}
/*! rtl:end:ignore */
</style>
